/**
 * RFP Capability Gate Component
 *
 * Conditionally renders children based on RFP capability status.
 * Extends the base CapabilityGate with RFP-specific features.
 */

import React, { ReactNode } from 'react';
import { CapabilityGate } from './CapabilityGate';
import { useRFPCapabilities, RFP_CAPABILITIES } from '@/hooks/useRFPCapabilities';
import { RFPCapabilityName, getRFPCapabilityMetadata } from '@/types/rfpCapabilities';
import { Accessibility, Map, MessageSquare, Smartphone, Sparkles } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';

// ============================================================================
// TYPES
// ============================================================================

interface RFPCapabilityGateProps {
  /** The organization ID to check capabilities for */
  organizationId: number | null;

  /** The RFP capability to check */
  capability: RFPCapabilityName;

  /** Content to render when capability is enabled */
  children: ReactNode;

  /** Content to render when capability is disabled (optional) */
  fallback?: ReactNode;

  /** Show loading state while checking capability */
  showLoading?: boolean;

  /** Behavior when capability is disabled */
  behavior?: 'hide' | 'disable' | 'show-upgrade';

  /** Custom upgrade message (overrides default from metadata) */
  upgradeMessage?: string;

  /** Callback when upgrade prompt is clicked */
  onUpgradeClick?: () => void;
}

// ============================================================================
// HELPER: CATEGORY ICON
// ============================================================================

function getCategoryIcon(category: string) {
  switch (category) {
    case 'accessibility':
      return <Accessibility className="w-6 h-6" />;
    case 'experience':
      return <Map className="w-6 h-6" />;
    case 'communication':
      return <MessageSquare className="w-6 h-6" />;
    case 'mobile':
      return <Smartphone className="w-6 h-6" />;
    default:
      return <Sparkles className="w-6 h-6" />;
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function RFPCapabilityGate({
  organizationId,
  capability,
  children,
  fallback,
  showLoading = true,
  behavior = 'hide',
  upgradeMessage,
  onUpgradeClick,
}: RFPCapabilityGateProps) {
  const { t, language } = useAppTranslation();
  const metadata = getRFPCapabilityMetadata(capability);
  const isSpanish = language === 'es';

  // Generate upgrade message from metadata if not provided
  const effectiveUpgradeMessage =
    upgradeMessage ||
    (metadata
      ? isSpanish
        ? `${metadata.labelEs}: ${metadata.descriptionEs}`
        : `${metadata.label}: ${metadata.description}`
      : t('common.feature_not_available', 'This feature is not available for your organization.'));

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability={capability}
      showLoading={showLoading}
      behavior={behavior}
      upgradeMessage={effectiveUpgradeMessage}
      onUpgradeClick={onUpgradeClick}
      fallback={fallback}
    >
      {children}
    </CapabilityGate>
  );
}

// ============================================================================
// MULTI-CAPABILITY GATE
// ============================================================================

interface RFPMultiCapabilityGateProps {
  /** The organization ID to check capabilities for */
  organizationId: number | null;

  /** The RFP capabilities to check */
  capabilities: RFPCapabilityName[];

  /** Require all capabilities (AND) or any capability (OR) */
  requireAll?: boolean;

  /** Content to render when capability check passes */
  children: ReactNode;

  /** Content to render when capability check fails */
  fallback?: ReactNode;

  /** Show loading state */
  showLoading?: boolean;

  /** Behavior when disabled */
  behavior?: 'hide' | 'disable' | 'show-upgrade';
}

export function RFPMultiCapabilityGate({
  organizationId,
  capabilities,
  requireAll = true,
  children,
  fallback,
  showLoading = true,
  behavior = 'hide',
}: RFPMultiCapabilityGateProps) {
  const { hasCapability, loading } = useRFPCapabilities(organizationId);

  if (loading && showLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const hasAccess = requireAll
    ? capabilities.every(hasCapability)
    : capabilities.some(hasCapability);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (behavior === 'hide') {
    return fallback ? <>{fallback}</> : null;
  }

  // For disable/show-upgrade, delegate to single capability gate with first missing capability
  const missingCapability = capabilities.find((cap) => !hasCapability(cap));
  if (missingCapability) {
    return (
      <RFPCapabilityGate
        organizationId={organizationId}
        capability={missingCapability}
        behavior={behavior}
        showLoading={false}
      >
        {children}
      </RFPCapabilityGate>
    );
  }

  return <>{children}</>;
}

// ============================================================================
// PHASE GATES
// ============================================================================

interface RFPPhaseGateProps {
  organizationId: number | null;
  phase: 1 | 2 | 3 | 4;
  children: ReactNode;
  fallback?: ReactNode;
  behavior?: 'hide' | 'disable' | 'show-upgrade';
}

export function RFPPhaseGate({
  organizationId,
  phase,
  children,
  fallback,
  behavior = 'hide',
}: RFPPhaseGateProps) {
  const { hasPhase1, hasPhase2, hasPhase3, hasPhase4, loading } =
    useRFPCapabilities(organizationId);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const phaseEnabled =
    (phase === 1 && hasPhase1) ||
    (phase === 2 && hasPhase2) ||
    (phase === 3 && hasPhase3) ||
    (phase === 4 && hasPhase4);

  if (phaseEnabled) {
    return <>{children}</>;
  }

  if (behavior === 'hide') {
    return fallback ? <>{fallback}</> : null;
  }

  // Show upgrade prompt for the phase
  return (
    <div className="p-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
        <Sparkles className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold mb-2">Phase {phase} Features</h3>
      <p className="text-gray-600 mb-4">
        Enable Phase {phase} capabilities to access these features.
      </p>
    </div>
  );
}

// ============================================================================
// CONVENIENCE GATES
// ============================================================================

/** Gate for accessibility features */
export function AccessibilityGate({
  organizationId,
  children,
  fallback,
  behavior = 'hide',
}: Omit<RFPCapabilityGateProps, 'capability'>) {
  return (
    <RFPCapabilityGate
      organizationId={organizationId}
      capability={RFP_CAPABILITIES.ACCESSIBILITY_SETTINGS}
      behavior={behavior}
      fallback={fallback}
    >
      {children}
    </RFPCapabilityGate>
  );
}

/** Gate for role-based dashboards */
export function RoleDashboardGate({
  organizationId,
  children,
  fallback,
  behavior = 'hide',
}: Omit<RFPCapabilityGateProps, 'capability'>) {
  return (
    <RFPCapabilityGate
      organizationId={organizationId}
      capability={RFP_CAPABILITIES.ROLE_BASED_DASHBOARDS}
      behavior={behavior}
      fallback={fallback}
    >
      {children}
    </RFPCapabilityGate>
  );
}

/** Gate for guided tours */
export function GuidedTourGate({
  organizationId,
  children,
  fallback,
  behavior = 'hide',
}: Omit<RFPCapabilityGateProps, 'capability'>) {
  return (
    <RFPCapabilityGate
      organizationId={organizationId}
      capability={RFP_CAPABILITIES.GUIDED_TOURS}
      behavior={behavior}
      fallback={fallback}
    >
      {children}
    </RFPCapabilityGate>
  );
}

/** Gate for offline support */
export function OfflineSupportGate({
  organizationId,
  children,
  fallback,
  behavior = 'hide',
}: Omit<RFPCapabilityGateProps, 'capability'>) {
  return (
    <RFPCapabilityGate
      organizationId={organizationId}
      capability={RFP_CAPABILITIES.OFFLINE_SUPPORT}
      behavior={behavior}
      fallback={fallback}
    >
      {children}
    </RFPCapabilityGate>
  );
}

/** Gate for bilingual templates */
export function BilingualTemplateGate({
  organizationId,
  children,
  fallback,
  behavior = 'hide',
}: Omit<RFPCapabilityGateProps, 'capability'>) {
  return (
    <RFPCapabilityGate
      organizationId={organizationId}
      capability={RFP_CAPABILITIES.BILINGUAL_TEMPLATES}
      behavior={behavior}
      fallback={fallback}
    >
      {children}
    </RFPCapabilityGate>
  );
}

export default RFPCapabilityGate;
